import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Typography,
  Popconfirm,
  Tag,
  Tooltip,
  Card,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  EyeOutlined
} from '@ant-design/icons';
import * as api from '@services/api';
import { BusinessCardForm, Preview } from '@components/BusinessCard';

const { Title } = Typography;

const BusinessCards = () => {
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [previewRecord, setPreviewRecord] = useState(null);
  const [searchForm] = Form.useForm();
  const [formLoading, setFormLoading] = useState(false);

  // 获取名片列表
  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await api.getBusinessCards({
        page: params.current || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        name: params.name,
        phone: params.phone
      });
      setData(response.list);
      setPagination(response.pagination);
    } catch (error) {
      message.error('获取名片列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 首次加载
  useEffect(() => {
    fetchData();
  }, []);

  // 处理搜索
  const handleSearch = async (values) => {
    const params = {
      ...values,
      current: 1
    };
    fetchData(params);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    fetchData({ current: 1 });
  };

  // 处理查看名片
  const handleView = (record) => {
    setPreviewRecord(record);
    setPreviewVisible(true);
  };

  // 表格列配置
  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      fixed: 'left',
      align: 'center',
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 100,
      fixed: 'left',
      align: 'center',
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
      fixed: 'left',
      align: 'center',
    },
    {
      title: '职位',
      dataIndex: 'position',
      key: 'position',
      width: 200,
      align: 'center',
    },
    {
      title: '单位',
      dataIndex: 'company',
      key: 'company',
      width: 200,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          <div style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center'
          }}>
            {text}
          </div>
        </Tooltip>
      )
    },
    {
      title: '模板类型',
      dataIndex: 'templateType',
      key: 'templateType',
      width: 100,
      align: 'center',
    },
    {
      title: '监督电话',
      dataIndex: 'supervisionPhone',
      key: 'supervisionPhone',
      width: 200,
      align: 'center',
    },
    {
      title: '营业所站地址',
      key: 'location',
      width: 200,
      ellipsis: true,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={record.location?.address || record.address}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              type="link"
              icon={<EnvironmentOutlined />}
              // onClick={() => handleViewLocation(record)}
              style={{ padding: 0 }}
            >
              {record.location?.address.slice(0, 10) + '...' || record.address.slice(0, 10) + '...'}
            </Button>
          </div>
        </Tooltip>
      )
    },
    {
      title: '服务范围',
      dataIndex: 'serviceArea',
      key: 'serviceArea',
      width: 200,
      ellipsis: true,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          <div style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center'
          }}>
            {text}
          </div>
        </Tooltip>
      )
    },
    {
      title: '绑定状态',
      dataIndex: 'openid',
      key: 'openid',
      width: 100,
      ellipsis: true,
      align: 'center',
      render: (_, record) => (
        <Tag color={record.openid ? 'success' : 'warning'}>
          {record.openid ? '已绑定' : '未绑定'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            查看
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingRecord(record);
              setModalVisible(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条名片吗？"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 处理表格变化
  const handleTableChange = (newPagination) => {
    const params = {
      ...searchForm.getFieldsValue(),
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    };
    fetchData(params);
  };

  // 处理删除
  const handleDelete = async (id) => {
    try {
      await api.deleteBusinessCard(id);
      message.success('删除成功');
      fetchData(searchForm.getFieldsValue());
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 处理表单提交
  const handleSubmit = async (values) => {
    try {
      setFormLoading(true);
      if (editingRecord) {
        await api.updateBusinessCard(editingRecord.id, values);
        message.success('更新成功');
      } else {
        const result = await api.createBusinessCard(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchData(searchForm.getFieldsValue());
    } catch (error) {
      if (error.errorFields) {
        return; // 表单验证错误
      }
      message.error(editingRecord ? '更新失败' : '创建失败');
    } finally {
      setFormLoading(false);
    }
  };

  // 处理查看位置
  const handleViewLocation = (record) => {
    if (record.location?.latitude && record.location?.longitude) {
      window.open(`https://map.qq.com/api/js/v2/marker?marker=coord:${record.location.latitude},${record.location.longitude};title:${record.name};addr:${record.location.address}`);
    } else {
      message.info('暂无位置信息');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={<Title level={2} style={{ margin: 0 }}>名片管理</Title>}
        style={{ marginBottom: '24px' }}
      >
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 0 }}
        >
          <Form.Item name="name">
            <Input placeholder="姓名" allowClear />
          </Form.Item>
          <Form.Item name="phone">
            <Input placeholder="电话" allowClear />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card
        style={{ background: '#fff' }}
      >
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography.Text type="secondary">
            共 {pagination.total} 条记录
          </Typography.Text>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingRecord(null);
              setModalVisible(true);
            }}
          >
            新建名片
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            ...pagination,
            showTotal: (total) => `共 ${total} 条`,
            showSizeChanger: true,
            showQuickJumper: true,
            style: { marginTop: '16px' }
          }}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑名片' : '新建名片'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
        }}
        destroyOnClose
        footer={null}
        width={1000}
      >
        <BusinessCardForm
          key={editingRecord ? editingRecord.id : 'new'}
          initialValues={editingRecord || {}}
          onSubmit={handleSubmit}
          loading={formLoading}
          onCancel={() => {
            setModalVisible(false);
            setEditingRecord(null);
          }}
        />
      </Modal>

      <Preview
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        data={previewRecord}
        templateType={previewRecord?.templateType || 1}
      />
    </div>
  );
};

export default BusinessCards; 